import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const DeploymentGuide = () => {
  const [activeEnvironment, setActiveEnvironment] = useState('development');
  const [checkedItems, setCheckedItems] = useState({});

  // Show environment section
  const showEnvironment = (envId) => {
    setActiveEnvironment(envId);
  };

  // Copy code function
  const copyCode = (button) => {
    const codeBlock = button.parentElement.querySelector('pre');
    const text = codeBlock.textContent;
    
    navigator.clipboard.writeText(text).then(() => {
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.style.background = 'rgba(103, 232, 169, 0.3)';
      button.style.color = '#67e8a9';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.style.color = '';
      }, 2000);
    });
  };

  // Toggle checklist item
  const toggleCheck = (index) => {
    setCheckedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Animate cards on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });
    
    document.querySelectorAll('.step-card, .requirement-card').forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [activeEnvironment]);

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Environment Tabs */
        .env-tabs {
            display: flex;
            justify-content: center;
            gap: 1rem;
            margin-bottom: 3rem;
            flex-wrap: wrap;
        }
        
        .env-tab {
            padding: 0.8rem 2rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: #888;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 1rem;
            font-weight: 600;
        }
        
        .env-tab:hover {
            background: rgba(255, 255, 255, 0.1);
            color: white;
        }
        
        .env-tab.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-color: transparent;
        }
        
        /* Deployment Sections */
        .deployment-section {
            display: none;
            animation: fadeIn 0.5s ease;
        }
        
        .deployment-section.active {
            display: block;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        /* Step Cards */
        .steps-container {
            margin: 3rem 0;
        }
        
        .step-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 2rem;
            transition: all 0.3s;
        }
        
        .step-card:hover {
            transform: translateX(10px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .step-header {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            margin-bottom: 1.5rem;
        }
        
        .step-number {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            font-weight: bold;
        }
        
        .step-title {
            flex: 1;
        }
        
        .step-title h3 {
            font-size: 1.5rem;
            margin-bottom: 0.3rem;
        }
        
        .step-duration {
            color: #888;
            font-size: 0.9rem;
        }
        
        .step-content {
            margin-left: 65px;
        }
        
        /* Code Blocks */
        .code-block {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 8px;
            padding: 1.5rem;
            margin: 1rem 0;
            overflow-x: auto;
            position: relative;
        }
        
        .code-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #333;
        }
        
        .code-lang {
            color: #667eea;
            font-size: 0.9rem;
            font-weight: 600;
        }
        
        .copy-btn {
            position: absolute;
            top: 1rem;
            right: 1rem;
            background: rgba(102, 126, 234, 0.2);
            border: 1px solid rgba(102, 126, 234, 0.3);
            color: #667eea;
            padding: 0.4rem 0.8rem;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.8rem;
            transition: all 0.3s;
        }
        
        .copy-btn:hover {
            background: rgba(102, 126, 234, 0.3);
        }
        
        .code-block pre {
            margin: 0;
            color: #e0e0e0;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 0.9rem;
            line-height: 1.6;
        }
        
        /* Requirements Grid */
        .requirements-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin: 2rem 0;
        }
        
        .requirement-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
        }
        
        .requirement-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .req-icon {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            margin-bottom: 1rem;
        }
        
        .req-title {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 1rem;
        }
        
        .req-specs {
            list-style: none;
        }
        
        .req-specs li {
            padding: 0.4rem 0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .req-specs li:last-child {
            border-bottom: none;
        }
        
        .req-value {
            color: #667eea;
            font-weight: 600;
        }
        
        /* Architecture Diagram */
        .architecture-diagram {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
        }
        
        .deployment-flow {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 2rem 0;
            flex-wrap: wrap;
            gap: 2rem;
        }
        
        .deploy-component {
            flex: 1;
            min-width: 150px;
            text-align: center;
            padding: 1.5rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            transition: all 0.3s;
            position: relative;
        }
        
        .deploy-component:hover {
            transform: scale(1.05);
            border-color: rgba(102, 126, 234, 0.5);
        }
        
        .deploy-component::after {
            content: '→';
            position: absolute;
            right: -1.5rem;
            top: 50%;
            transform: translateY(-50%);
            font-size: 1.5rem;
            color: #667eea;
        }
        
        .deploy-component:last-child::after {
            display: none;
        }
        
        .deploy-icon {
            font-size: 2rem;
            margin-bottom: 0.5rem;
        }
        
        .deploy-name {
            font-weight: 600;
            margin-bottom: 0.3rem;
        }
        
        .deploy-status {
            font-size: 0.8rem;
            color: #888;
        }
        
        /* Alert Boxes */
        .alert-box {
            padding: 1.5rem;
            border-radius: 12px;
            margin: 2rem 0;
            display: flex;
            align-items: start;
            gap: 1rem;
        }
        
        .alert-info {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
        }
        
        .alert-warning {
            background: rgba(237, 137, 54, 0.1);
            border: 1px solid rgba(237, 137, 54, 0.3);
        }
        
        .alert-success {
            background: rgba(103, 232, 169, 0.1);
            border: 1px solid rgba(103, 232, 169, 0.3);
        }
        
        .alert-icon {
            font-size: 1.5rem;
            flex-shrink: 0;
            margin-top: 0.2rem;
        }
        
        .alert-content h4 {
            margin-bottom: 0.5rem;
        }
        
        /* Checklist */
        .checklist {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .checklist-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 0.8rem 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .checklist-item:last-child {
            border-bottom: none;
        }
        
        .checklist-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s;
        }
        
        .checklist-checkbox.checked {
            background: #67e8a9;
            border-color: #67e8a9;
        }
        
        .checklist-checkbox.checked::after {
            content: '✓';
            color: #0a0a0a;
            font-weight: bold;
        }
        
        /* Troubleshooting */
        .troubleshooting-section {
            background: rgba(245, 101, 101, 0.05);
            border: 1px solid rgba(245, 101, 101, 0.2);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
        }
        
        .trouble-item {
            margin-bottom: 2rem;
            padding-bottom: 2rem;
            border-bottom: 1px solid rgba(245, 101, 101, 0.1);
        }
        
        .trouble-item:last-child {
            border-bottom: none;
            margin-bottom: 0;
        }
        
        .trouble-problem {
            color: #f56565;
            font-weight: 600;
            margin-bottom: 0.5rem;
        }
        
        .trouble-solution {
            color: #bbb;
            margin-left: 1rem;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .env-tabs {
                flex-direction: column;
            }
            
            .deployment-flow {
                flex-direction: column;
            }
            
            .deploy-component::after {
                display: none;
            }
            
            .requirements-grid {
                grid-template-columns: 1fr;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Guide de Déploiement</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Guide de Déploiement</h1>
          <p className="page-subtitle">Installation et configuration de l'Agent RAG Hybride Banking</p>
        </div>
        
        {/* Environment Tabs */}
        <div className="env-tabs">
          <button 
            className={`env-tab ${activeEnvironment === 'development' ? 'active' : ''}`} 
            onClick={() => showEnvironment('development')}
          >
            Développement
          </button>
          <button 
            className={`env-tab ${activeEnvironment === 'staging' ? 'active' : ''}`} 
            onClick={() => showEnvironment('staging')}
          >
            Staging
          </button>
          <button 
            className={`env-tab ${activeEnvironment === 'production' ? 'active' : ''}`} 
            onClick={() => showEnvironment('production')}
          >
            Production
          </button>
        </div>
        
        {/* Development Environment */}
        <div className={`deployment-section ${activeEnvironment === 'development' ? 'active' : ''}`} id="development">
          {/* Requirements */}
          <h2>Prérequis Système</h2>
          <div className="requirements-grid">
            <div className="requirement-card">
              <div className="req-icon">💻</div>
              <h3 className="req-title">Hardware Minimum</h3>
              <ul className="req-specs">
                <li><span>CPU</span><span className="req-value">4 cores</span></li>
                <li><span>RAM</span><span className="req-value">16 GB</span></li>
                <li><span>Stockage</span><span className="req-value">100 GB SSD</span></li>
                <li><span>Réseau</span><span className="req-value">1 Gbps</span></li>
              </ul>
            </div>
            
            <div className="requirement-card">
              <div className="req-icon">🐳</div>
              <h3 className="req-title">Software Requis</h3>
              <ul className="req-specs">
                <li><span>Docker</span><span className="req-value">≥ 24.0</span></li>
                <li><span>Docker Compose</span><span className="req-value">≥ 2.20</span></li>
                <li><span>Python</span><span className="req-value">3.11+</span></li>
                <li><span>Git</span><span className="req-value">Latest</span></li>
              </ul>
            </div>
            
            <div className="requirement-card">
              <div className="req-icon">🔑</div>
              <h3 className="req-title">API Keys</h3>
              <ul className="req-specs">
                <li><span>OpenAI API</span><span className="req-value">GPT-4</span></li>
                <li><span>Anthropic</span><span className="req-value">Claude 3</span></li>
                <li><span>Elasticsearch</span><span className="req-value">Optional</span></li>
                <li><span>Monitoring</span><span className="req-value">Optional</span></li>
              </ul>
            </div>
          </div>
          
          {/* Installation Steps */}
          <div className="steps-container">
            <h2>Installation Développement</h2>
            
            <div className="step-card">
              <div className="step-header">
                <div className="step-number">1</div>
                <div className="step-title">
                  <h3>Clonage du Repository</h3>
                  <div className="step-duration">~2 minutes</div>
                </div>
              </div>
              <div className="step-content">
                <p>Récupération du code source et des configurations de base.</p>
                
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">bash</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# Clone du repository principal
git clone https://github.com/your-org/rag-hybride-banking.git
cd rag-hybride-banking

# Vérification des branches
git branch -a

# Switch vers branche dev
git checkout develop`}</pre>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-header">
                <div className="step-number">2</div>
                <div className="step-title">
                  <h3>Configuration Environnement</h3>
                  <div className="step-duration">~5 minutes</div>
                </div>
              </div>
              <div className="step-content">
                <p>Configuration des variables d'environnement et secrets.</p>
                
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">bash</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# Copie du template d'environnement
cp .env.template .env.dev

# Edition des variables (utilisez votre éditeur préféré)
nano .env.dev`}</pre>
                </div>
                
                <div className="alert-box alert-info">
                  <div className="alert-icon">ℹ️</div>
                  <div className="alert-content">
                    <h4>Variables importantes</h4>
                    <p>Assurez-vous de configurer au minimum OPENAI_API_KEY, REDIS_URL et DATABASE_URL</p>
                  </div>
                </div>
                
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">env</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# API Keys
OPENAI_API_KEY=sk-your-openai-key
ANTHROPIC_API_KEY=your-anthropic-key

# Base de données
DATABASE_URL=postgresql://user:pass@localhost:5432/ragdb
REDIS_URL=redis://localhost:6379

# Configuration RAG
EMBEDDING_MODEL=sentence-transformers/all-MiniLM-L6-v2
LLM_MODEL=gpt-4-turbo-preview
CHUNK_SIZE=1000
CHUNK_OVERLAP=200

# Vector DB
FAISS_INDEX_PATH=./data/faiss_index
QDRANT_URL=http://localhost:6333

# Monitoring
LOG_LEVEL=INFO
METRICS_ENABLED=true`}</pre>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-header">
                <div className="step-number">3</div>
                <div className="step-title">
                  <h3>Lancement des Services</h3>
                  <div className="step-duration">~10 minutes</div>
                </div>
              </div>
              <div className="step-content">
                <p>Démarrage de l'infrastructure avec Docker Compose.</p>
                
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">bash</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# Construction des images
docker-compose -f docker-compose.dev.yml build

# Lancement des services en arrière-plan
docker-compose -f docker-compose.dev.yml up -d

# Vérification du statut
docker-compose ps`}</pre>
                </div>
                
                <div className="deployment-flow">
                  <div className="deploy-component">
                    <div className="deploy-icon">💾</div>
                    <div className="deploy-name">Redis</div>
                    <div className="deploy-status">Port 6379</div>
                  </div>
                  <div className="deploy-component">
                    <div className="deploy-icon">🔍</div>
                    <div className="deploy-name">Elasticsearch</div>
                    <div className="deploy-status">Port 9200</div>
                  </div>
                  <div className="deploy-component">
                    <div className="deploy-icon">🕸️</div>
                    <div className="deploy-name">Neo4j</div>
                    <div className="deploy-status">Port 7474</div>
                  </div>
                  <div className="deploy-component">
                    <div className="deploy-icon">🐘</div>
                    <div className="deploy-name">PostgreSQL</div>
                    <div className="deploy-status">Port 5432</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-header">
                <div className="step-number">4</div>
                <div className="step-title">
                  <h3>Initialisation des Données</h3>
                  <div className="step-duration">~15 minutes</div>
                </div>
              </div>
              <div className="step-content">
                <p>Création des index et chargement des données de test.</p>
                
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">bash</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# Installation des dépendances Python
pip install -r requirements.txt

# Initialisation de la base de données
python scripts/init_database.py

# Création des index vectoriels
python scripts/create_vector_index.py

# Chargement des documents de test
python scripts/load_sample_data.py`}</pre>
                </div>
                
                <div className="alert-box alert-warning">
                  <div className="alert-icon">⚠️</div>
                  <div className="alert-content">
                    <h4>Attention</h4>
                    <p>Le premier chargement peut prendre du temps car il génère les embeddings pour tous les documents.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-header">
                <div className="step-number">5</div>
                <div className="step-title">
                  <h3>Lancement de l'Application</h3>
                  <div className="step-duration">~2 minutes</div>
                </div>
              </div>
              <div className="step-content">
                <p>Démarrage du serveur API et vérification du fonctionnement.</p>
                
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">bash</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# Lancement en mode dev avec hot-reload
uvicorn app.main:app --reload --host 0.0.0.0 --port 5000

# Ou via Docker
docker-compose -f docker-compose.dev.yml up app`}</pre>
                </div>
                
                <div className="alert-box alert-success">
                  <div className="alert-icon">✅</div>
                  <div className="alert-content">
                    <h4>Vérification</h4>
                    <p>L'API devrait être accessible sur <strong>http://localhost:5000</strong><br/>
                    Documentation Swagger : <strong>http://localhost:5000/docs</strong></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Verification Checklist */}
          <div className="checklist">
            <h3 style={{marginBottom: '1.5rem'}}>✅ Checklist de Vérification</h3>
            
            <div className="checklist-item">
              <div 
                className={`checklist-checkbox ${checkedItems[0] ? 'checked' : ''}`} 
                onClick={() => toggleCheck(0)}
              ></div>
              <span>Tous les services Docker sont UP (redis, elasticsearch, neo4j, postgres)</span>
            </div>
            
            <div className="checklist-item">
              <div 
                className={`checklist-checkbox ${checkedItems[1] ? 'checked' : ''}`} 
                onClick={() => toggleCheck(1)}
              ></div>
              <span>L'API répond sur /health avec status 200</span>
            </div>
            
            <div className="checklist-item">
              <div 
                className={`checklist-checkbox ${checkedItems[2] ? 'checked' : ''}`} 
                onClick={() => toggleCheck(2)}
              ></div>
              <span>Les index vectoriels sont créés (vérifier les logs)</span>
            </div>
            
            <div className="checklist-item">
              <div 
                className={`checklist-checkbox ${checkedItems[3] ? 'checked' : ''}`} 
                onClick={() => toggleCheck(3)}
              ></div>
              <span>Une requête test sur /query retourne une réponse</span>
            </div>
            
            <div className="checklist-item">
              <div 
                className={`checklist-checkbox ${checkedItems[4] ? 'checked' : ''}`} 
                onClick={() => toggleCheck(4)}
              ></div>
              <span>Les métriques Prometheus sont accessibles</span>
            </div>
            
            <div className="checklist-item">
              <div 
                className={`checklist-checkbox ${checkedItems[5] ? 'checked' : ''}`} 
                onClick={() => toggleCheck(5)}
              ></div>
              <span>La documentation Swagger est accessible</span>
            </div>
          </div>
        </div>
        
        {/* Staging Environment */}
        <div className={`deployment-section ${activeEnvironment === 'staging' ? 'active' : ''}`} id="staging">
          <h2>Déploiement Staging</h2>
          
          <div className="alert-box alert-info">
            <div className="alert-icon">🎯</div>
            <div className="alert-content">
              <h4>Objectif Staging</h4>
              <p>Environnement de validation avant production. Réplique la production avec des données de test.</p>
            </div>
          </div>
          
          <div className="steps-container">
            <div className="step-card">
              <div className="step-header">
                <div className="step-number">1</div>
                <div className="step-title">
                  <h3>Préparation Serveur</h3>
                  <div className="step-duration">~30 minutes</div>
                </div>
              </div>
              <div className="step-content">
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">bash</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# Mise à jour du système
sudo apt update && sudo apt upgrade -y

# Installation Docker
curl -fsSL https://get.docker.com -o get-docker.sh
sudo sh get-docker.sh
sudo usermod -aG docker $USER

# Installation Docker Compose
sudo curl -L "https://github.com/docker/compose/releases/latest/download/docker-compose-$(uname -s)-$(uname -m)" -o /usr/local/bin/docker-compose
sudo chmod +x /usr/local/bin/docker-compose

# Installation des outils de monitoring
sudo apt install -y htop iotop nethogs`}</pre>
                </div>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-header">
                <div className="step-number">2</div>
                <div className="step-title">
                  <h3>Configuration SSL/TLS</h3>
                  <div className="step-duration">~15 minutes</div>
                </div>
              </div>
              <div className="step-content">
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-lang">bash</span>
                  </div>
                  <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
                  <pre>{`# Installation Certbot
sudo apt install -y certbot python3-certbot-nginx

# Génération certificat SSL
sudo certbot --nginx -d staging.rag-banking.company.com

# Configuration auto-renewal
sudo crontab -e
# Ajouter: 0 12 * * * /usr/bin/certbot renew --quiet`}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Production Environment */}
        <div className={`deployment-section ${activeEnvironment === 'production' ? 'active' : ''}`} id="production">
          <h2>Déploiement Production</h2>
          
          <div className="alert-box alert-warning">
            <div className="alert-icon">🚨</div>
            <div className="alert-content">
              <h4>Environnement Critique</h4>
              <p>Suivez scrupuleusement cette procédure. Tout changement doit être validé en staging d'abord.</p>
            </div>
          </div>
          
          <div className="requirements-grid">
            <div className="requirement-card">
              <div className="req-icon">🏗️</div>
              <h3 className="req-title">Infrastructure Production</h3>
              <ul className="req-specs">
                <li><span>CPU</span><span className="req-value">16+ cores</span></li>
                <li><span>RAM</span><span className="req-value">64+ GB</span></li>
                <li><span>Stockage</span><span className="req-value">500+ GB SSD</span></li>
                <li><span>Réseau</span><span className="req-value">10 Gbps</span></li>
              </ul>
            </div>
            
            <div className="requirement-card">
              <div className="req-icon">🛡️</div>
              <h3 className="req-title">Sécurité</h3>
              <ul className="req-specs">
                <li><span>Firewall</span><span className="req-value">UFW/iptables</span></li>
                <li><span>SSL/TLS</span><span className="req-value">Let's Encrypt</span></li>
                <li><span>VPN</span><span className="req-value">WireGuard</span></li>
                <li><span>Backup</span><span className="req-value">Daily</span></li>
              </ul>
            </div>
            
            <div className="requirement-card">
              <div className="req-icon">📊</div>
              <h3 className="req-title">Monitoring</h3>
              <ul className="req-specs">
                <li><span>Uptime</span><span className="req-value">99.9%</span></li>
                <li><span>Alertes</span><span className="req-value">24/7</span></li>
                <li><span>Logs</span><span className="req-value">30 jours</span></li>
                <li><span>Metrics</span><span className="req-value">1 an</span></li>
              </ul>
            </div>
          </div>
          
          {/* Kubernetes Deployment */}
          <div className="architecture-diagram">
            <h3>Architecture Kubernetes</h3>
            <div className="code-block">
              <div className="code-header">
                <span className="code-lang">yaml</span>
              </div>
              <button className="copy-btn" onClick={(e) => copyCode(e.target)}>Copy</button>
              <pre>{`apiVersion: apps/v1
kind: Deployment
metadata:
  name: rag-hybride-api
  namespace: rag-banking
spec:
  replicas: 3
  selector:
    matchLabels:
      app: rag-hybride-api
  template:
    metadata:
      labels:
        app: rag-hybride-api
    spec:
      containers:
      - name: api
        image: rag-hybride:v1.0.0
        ports:
        - containerPort: 5000
        env:
        - name: ENVIRONMENT
          value: "production"
        - name: REDIS_URL
          valueFrom:
            secretKeyRef:
              name: rag-secrets
              key: redis-url
        resources:
          requests:
            memory: "2Gi"
            cpu: "1000m"
          limits:
            memory: "4Gi"
            cpu: "2000m"
        livenessProbe:
          httpGet:
            path: /health
            port: 5000
          initialDelaySeconds: 30
          periodSeconds: 10
        readinessProbe:
          httpGet:
            path: /ready
            port: 5000
          initialDelaySeconds: 15
          periodSeconds: 5`}</pre>
            </div>
          </div>
        </div>
        
        {/* Troubleshooting */}
        <div className="troubleshooting-section">
          <h2>🔧 Résolution de Problèmes</h2>
          
          <div className="trouble-item">
            <div className="trouble-problem">Erreur: "Redis connection failed"</div>
            <div className="trouble-solution">
              Vérifiez que Redis est démarré : <code>docker-compose ps redis</code><br/>
              Testez la connexion : <code>redis-cli ping</code><br/>
              Vérifiez les variables d'environnement REDIS_URL
            </div>
          </div>
          
          <div className="trouble-item">
            <div className="trouble-problem">Erreur: "Elasticsearch cluster unhealthy"</div>
            <div className="trouble-solution">
              Vérifiez l'état du cluster : <code>curl http://localhost:9200/_cluster/health</code><br/>
              Redémarrez Elasticsearch : <code>docker-compose restart elasticsearch</code><br/>
              Vérifiez l'espace disque disponible (min 15% libre requis)
            </div>
          </div>
          
          <div className="trouble-item">
            <div className="trouble-problem">Performance dégradée (latence &gt; 3s)</div>
            <div className="trouble-solution">
              Vérifiez les métriques Prometheus : <code>http://localhost:9090</code><br/>
              Analysez les logs d'application : <code>docker-compose logs -f app</code><br/>
              Vérifiez la charge CPU/RAM : <code>docker stats</code><br/>
              Optimisez la taille des chunks ou le cache Redis
            </div>
          </div>
          
          <div className="trouble-item">
            <div className="trouble-problem">Erreur: "OpenAI API quota exceeded"</div>
            <div className="trouble-solution">
              Vérifiez votre quota OpenAI : <code>https://platform.openai.com/usage</code><br/>
              Implémentez un fallback vers Claude 3 : configurez ANTHROPIC_API_KEY<br/>
              Optimisez l'utilisation du cache pour réduire les appels API
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeploymentGuide;